import { Button, Card, Container, Eyebrow, GoldDivider } from '@tt/design-system';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { z } from 'zod';
import { OtpInput } from '../../components/form/OtpInput';
import { TextField } from '../../components/form/TextField';
import { OTP_EXAMPLE } from '../../lib/env';
import { useAuthStore } from '../../store/authStore';

const emailSchema = z.object({
  email: z
    .string()
    .min(1, 'Digite o seu e-mail.')
    .email('Esse e-mail não parece válido.'),
});

type EmailForm = z.infer<typeof emailSchema>;

type LocationState = { from?: Location };

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const requestOtp = useAuthStore((s) => s.requestOtp);
  const confirmOtp = useAuthStore((s) => s.confirmOtp);
  const pendingEmail = useAuthStore((s) => s.pendingEmail);
  const isRequesting = useAuthStore((s) => s.isRequesting);

  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpSubmitting, setOtpSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<EmailForm>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '' },
    mode: 'onSubmit',
  });

  async function onSubmitEmail(data: EmailForm) {
    setOtpError(null);
    setOtpValue('');
    await requestOtp(data.email);
  }

  async function onSubmitOtp() {
    setOtpError(null);
    if (otpValue.length !== 6) {
      setOtpError('Digite os 6 dígitos do código.');
      return;
    }
    setOtpSubmitting(true);
    const ok = await confirmOtp(otpValue);
    setOtpSubmitting(false);
    if (!ok) {
      setOtpError('Esse código não é válido. Tente novamente.');
      return;
    }
    const state = location.state as LocationState | null;
    const dest =
      state?.from?.pathname && state.from.pathname !== '/login'
        ? state.from.pathname
        : '/register';
    navigate(dest, { replace: true });
  }

  function handleResend() {
    const email = pendingEmail ?? getValues('email');
    setOtpValue('');
    setOtpError(null);
    if (email) requestOtp(email);
  }

  return (
    <Container size="narrow" className="w-full">
      <div className="flex flex-col items-center text-center">
        <Eyebrow tone="gold" className="mb-4">
          Sua conta cerimonial
        </Eyebrow>
        <h1 className="mb-3 font-display text-3xl leading-tight text-parchment md:text-4xl">
          Entre para tocar a taça.
        </h1>
        <p className="mb-10 max-w-md text-sm leading-relaxed text-neutral-100 md:text-base">
          Sua conta é criada automaticamente — sem senha pra lembrar. Use o código que enviamos
          para o seu e-mail.
        </p>
      </div>

      <Card tone="raised" padding="lg" className="mx-auto max-w-md">
        {!pendingEmail ? (
          <form
            onSubmit={handleSubmit(onSubmitEmail)}
            noValidate
            className="flex flex-col gap-6"
            aria-label="Entrar com e-mail"
          >
            <TextField
              label="Seu e-mail"
              type="email"
              autoComplete="email"
              placeholder="seu@email.com"
              hint="Vamos enviar um código de 6 dígitos para esse endereço."
              error={errors.email?.message}
              autoFocus
              {...register('email')}
            />
            <Button type="submit" variant="primary" size="lg" fullWidth disabled={isRequesting}>
              {isRequesting ? 'Enviando código…' : 'Enviar código'}
            </Button>
          </form>
        ) : (
          <div className="flex flex-col gap-6" aria-label="Confirmar código de acesso">
            <div className="text-center">
              <Eyebrow tone="gold" className="mb-2">
                Código enviado
              </Eyebrow>
              <p className="text-sm leading-relaxed text-neutral-100">
                Verifique o seu e-mail em{' '}
                <span className="font-mono text-champagne">{pendingEmail}</span>.
              </p>
            </div>

            <OtpInput
              label="Código de 6 dígitos"
              value={otpValue}
              onChange={setOtpValue}
              error={otpError ?? undefined}
              hint={`Modo demonstração: ${OTP_EXAMPLE} (ou qualquer 6 dígitos) é aceito.`}
              autoFocus
              disabled={otpSubmitting}
            />

            <Button
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              onClick={onSubmitOtp}
              disabled={otpSubmitting}
            >
              {otpSubmitting ? 'Confirmando…' : 'Confirmar e entrar'}
            </Button>

            <GoldDivider variant="line" />

            <button
              type="button"
              onClick={handleResend}
              disabled={isRequesting}
              className="text-center font-mono text-xs uppercase tracking-stamp text-champagne transition-colors duration-fast hover:text-gold disabled:opacity-50"
            >
              {isRequesting ? 'Reenviando…' : 'Reenviar código'}
            </button>
          </div>
        )}
      </Card>

      <p className="mx-auto mt-8 max-w-md text-center text-xs italic leading-relaxed text-champagne/60">
        Ao continuar, você concorda com nossos termos de uso e política de privacidade.
        Dados do registro são públicos; e-mail permanece privado.
      </p>
    </Container>
  );
}
